/**
 * engine/mixer.ts — the pult's mixer in the context: each deck's chain of sound, MAIN and its meter
 * (task 56).
 *
 * `docs/superpowers/specs/2026-10-05-pult-design.md` «Цепочка звука деки, микшер, автомат». Built
 * through the seam a node at a time (`engine/scratch.ts`, `ScratchContext`), so the test's recorder
 * sees every node, every connection and every number in order, and the browser decides nothing:
 *
 * element → head → LOW → MID → HIGH → FILTER → the deck's gain → MAIN → meter → speaker
 *                                                  ├→ four sends → ECHO, REVERB, FLANGER, PHASER → DEPTH → MAIN
 *                                                  └→ ECHO OUT's tail → MAIN
 *
 * `head` is a gain left at 1 — the door of the chain: the element's sound comes in there, and from
 * task 57 the scratch voice of the same deck, so a scratch has the deck's tone, filter and fader.
 * The numbers are `lib/pult-mixer.ts`'s. Every number glides ({@link GAIN_GLIDE_S}): the player
 * writes a gain every 50 ms, a finger moves a knob a hundred times a second, and a step would click.
 *
 * Task 58: the four effects of each deck sit after its gain, each behind a send, and come back
 * into MAIN through one gain of DEPTH beside the dry sound — why after the gain and not at the door,
 * and their numbers, are `lib/pult-effects.ts`'s. All of them are built with the deck, once: a send
 * that closes leaves the effect to ring out by itself, and a node made or torn down under the music
 * could click.
 */

import {
  DEFAULT_BPM,
  FLANGER,
  PHASER,
  PULT_EFFECTS,
  deckEffects,
  reverbResponse,
} from '../lib/pult-effects';
import type { DeckEffectSettings, PultEffect } from '../lib/pult-effects';
import { TONE_BANDS, filterSettings, toneSettings } from '../lib/pult-mixer';
import type { ToneBand } from '../lib/pult-mixer';

import type { MediaElementLike } from './element';
import type { ScratchContext, SoundNode } from './scratch';

/** The time constant every value of the mixer glides with: 95 % of the way in 60 ms. */
export const GAIN_GLIDE_S = 0.02;

/** One deck's chain. */
export interface MixerDeck {
  /** The door of the chain — see the header. */
  readonly head: SoundNode;
  /**
   * The deck's gain: LEVEL × fader × crossfader × the sleep timer's share (`engine/player.ts`) —
   * gliding by {@link GAIN_GLIDE_S}, or by `glideS` (ECHO OUT's 50 ms, task 58).
   */
  gain(value: number, glideS?: number): void;
  /** A tone knob at `position`, 0…1. */
  tone(band: ToneBand, position: number): void;
  /** FILTER at `position`, 0…1. */
  filter(position: number): void;
  /** Task 58: the deck's four effects (`lib/pult-effects.ts`, `deckEffects`). */
  effects(settings: DeckEffectSettings): void;
}

/** Task 58: the effects of a deck nobody has touched — every send and the tail shut. */
const AT_REST = deckEffects({
  effect: 'echo',
  lever: 0,
  beats: 0.5,
  depth: 0.5,
  bpm: DEFAULT_BPM,
  tail: false,
});

export interface Mixer {
  /** A deck's element into its chain, for good. Throws when the browser refuses its source. */
  deck(el: MediaElementLike): MixerDeck;
  /** MAIN's level now, 0…1. */
  level(): number;
}

const BANDS = Object.keys(TONE_BANDS) as ToneBand[];

export function createMixer(context: ScratchContext): Mixer {
  const main = context.node('gain');
  const meter = context.node('analyser');
  context.connect(main, meter);
  context.connect(meter, 'speaker');

  return {
    deck(el: MediaElementLike): MixerDeck {
      const source = context.source(el);
      const head = context.node('gain');
      context.connect(source, head);
      const tones = new Map<ToneBand, SoundNode>();
      let last: SoundNode = head;
      for (const band of BANDS) {
        const node = context.node('biquad');
        const settings = toneSettings(band, 0.5);
        context.set(node, 'type', settings.type);
        context.set(node, 'frequency', settings.frequency);
        context.set(node, 'Q', settings.q);
        context.set(node, 'gain', settings.gainDb);
        context.connect(last, node);
        tones.set(band, node);
        last = node;
      }
      const filter = context.node('biquad');
      const open = filterSettings(0.5);
      context.set(filter, 'type', open.type);
      context.set(filter, 'frequency', open.frequency);
      context.set(filter, 'Q', open.q);
      context.connect(last, filter);
      const gain = context.node('gain');
      context.connect(filter, gain);
      // The dry way first. Nothing in the browser hangs on the order; the test's recorder follows
      // a chain by a node's first way out (`test/helpers/fake-scratch.ts`, `chainOf`).
      context.connect(gain, main);
      const effects = buildEffects(context, gain, main);
      let filterType: string = open.type;
      // What each knob was last written at: a write of the same says nothing, and the player
      // passes every knob on every change of any.
      const written = new Map<string, number>([
        ['low', 0.5],
        ['mid', 0.5],
        ['high', 0.5],
        ['filter', 0.5],
      ]);

      return {
        head,
        gain(value: number, glideS = GAIN_GLIDE_S): void {
          context.set(gain, 'gain', value, glideS);
        },
        tone(band: ToneBand, position: number): void {
          const node = tones.get(band);
          if (node === undefined || written.get(band) === position) return;
          written.set(band, position);
          context.set(node, 'gain', toneSettings(band, position).gainDb, GAIN_GLIDE_S);
        },
        filter(position: number): void {
          if (written.get('filter') === position) return;
          written.set('filter', position);
          const settings = filterSettings(position);
          // A type changes once, where the knob crosses the middle, and its frequency with it at
          // once: a highpass left gliding down from a lowpass's 20 kHz would cut the whole sound
          // for 60 ms. Otherwise the frequency glides.
          if (settings.type !== filterType) {
            filterType = settings.type;
            context.set(filter, 'type', settings.type);
            context.set(filter, 'frequency', settings.frequency);
            return;
          }
          context.set(filter, 'frequency', settings.frequency, GAIN_GLIDE_S);
        },
        effects,
      };
    },
    level(): number {
      return context.level(meter);
    },
  };
}

/**
 * Task 58: a deck's four effects after its gain `from`, into `main`, and ECHO OUT's tail — set at
 * rest — and the way to set them. Each number is written only when it changes: the player passes
 * all of them on every move of any knob.
 */
function buildEffects(
  context: ScratchContext,
  from: SoundNode,
  main: SoundNode,
): (settings: DeckEffectSettings) => void {
  const wet = context.node('gain');
  context.connect(wet, main);
  const sends = new Map<PultEffect, SoundNode>();
  const openSend = (effect: PultEffect): SoundNode => {
    const send = context.node('gain');
    context.set(send, 'gain', 0);
    context.connect(from, send);
    sends.set(effect, send);
    return send;
  };

  // ECHO: a delay fed back into itself.
  const echo = context.node('delay');
  const echoBack = context.node('gain');
  context.connect(openSend('echo'), echo);
  context.connect(echo, echoBack);
  context.connect(echoBack, echo);
  context.connect(echo, wet);

  // REVERB: the hall's response, computed on the page.
  const hall = context.node('convolver');
  context.response(hall, reverbResponse(context.sampleRate));
  context.connect(openSend('reverb'), hall);
  context.connect(hall, wet);

  // FLANGER: a short delay, fed back, its length swung by an oscillator.
  const flanger = context.node('delay');
  const flangerBack = context.node('gain');
  const flangerLfo = context.node('oscillator');
  const flangerSweep = context.node('gain');
  context.set(flanger, 'delayTime', FLANGER.baseS);
  context.set(flangerBack, 'gain', FLANGER.feedback);
  context.set(flangerLfo, 'frequency', FLANGER.lfoHz);
  context.set(flangerSweep, 'gain', FLANGER.sweepS);
  context.connect(openSend('flanger'), flanger);
  context.connect(flanger, flangerBack);
  context.connect(flangerBack, flanger);
  context.connect(flangerLfo, flangerSweep);
  context.connect(flangerSweep, { node: flanger, param: 'delayTime' });
  context.connect(flanger, wet);

  // PHASER: four allpass filters in a row, all swung by one oscillator.
  const phaserLfo = context.node('oscillator');
  const phaserSweep = context.node('gain');
  context.set(phaserSweep, 'gain', PHASER.sweepHz);
  context.connect(phaserLfo, phaserSweep);
  let last = openSend('phaser');
  for (let i = 0; i < PHASER.stages; i += 1) {
    const stage = context.node('biquad');
    context.set(stage, 'type', 'allpass');
    context.set(stage, 'frequency', PHASER.centreHz);
    context.connect(last, stage);
    context.connect(phaserSweep, { node: stage, param: 'frequency' });
    last = stage;
  }
  context.connect(last, wet);

  // ECHO OUT's tail: fed by the deck all the time, its loop and its way out shut — it holds the
  // last of the track for the moment the deck goes silent (`lib/pult-effects.ts`).
  const tail = context.node('delay');
  const tailBack = context.node('gain');
  const tailGate = context.node('gain');
  context.set(tailBack, 'gain', 0);
  context.set(tailGate, 'gain', 0);
  context.connect(from, tail);
  context.connect(tail, tailBack);
  context.connect(tailBack, tail);
  context.connect(tail, tailGate);
  context.connect(tailGate, main);

  const written = new Map<string, number>([
    ...PULT_EFFECTS.map((effect): [string, number] => [`send.${effect}`, 0]),
    ['tail.gate', 0],
    ['tail.back', 0],
  ]);
  // The values at rest go in at once — nothing sounds through them yet; every later one glides.
  let built = false;
  const write = (key: string, node: SoundNode, name: string, value: number): void => {
    if (written.get(key) === value) return;
    written.set(key, value);
    context.set(node, name, value, built ? GAIN_GLIDE_S : 0);
  };
  const apply = (settings: DeckEffectSettings): void => {
    // The tail first: ECHO OUT opens it before the deck's gain goes (`engine/player.ts`).
    write('tail.gate', tailGate, 'gain', settings.tail.gate);
    write('tail.back', tailBack, 'gain', settings.tail.feedback);
    write('tail.delay', tail, 'delayTime', settings.echo.delayS);
    for (const effect of PULT_EFFECTS) {
      const send = sends.get(effect);
      if (send !== undefined) write(`send.${effect}`, send, 'gain', settings.sends[effect]);
    }
    write('wet', wet, 'gain', settings.wet);
    write('echo.delay', echo, 'delayTime', settings.echo.delayS);
    write('echo.back', echoBack, 'gain', settings.echo.feedback);
    write('phaser.lfo', phaserLfo, 'frequency', settings.phaserHz);
  };
  apply(AT_REST);
  built = true;
  return apply;
}
